// v3E.1 — Add Medicine Wizard
// Step 1: Source (catalog/manual/barcode/prescription/from-existing-rx)
// Step 2: Medicine details
// Step 3: Prescription details (optional)
// Step 4: Schedule
// Step 5: Refill tracking
// Step 6: Confirm
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, Loader2, Search, ScanLine, Pill, FileText, Pencil, Layers,
    CheckCircle2, Plus, Trash2, Sun, Coffee, Moon, Clock, ShieldAlert, X,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const SOURCES = [
    { id: "catalog", label: "Search store", icon: Search, blurb: "Pick from the PILLS catalog" },
    { id: "prescription_existing", label: "From a prescription", icon: FileText, blurb: "Auto-fill from a saved prescription" },
    { id: "barcode", label: "Scan barcode", icon: ScanLine, blurb: "Camera or manual entry of pack barcode" },
    { id: "manual", label: "Manual entry", icon: Pencil, blurb: "Type the medicine name yourself" },
];

const SLOT_PRESETS = [
    { label: "Before breakfast", time: "07:30", icon: Sun },
    { label: "After breakfast", time: "08:30", icon: Sun },
    { label: "Before lunch", time: "12:30", icon: Coffee },
    { label: "After lunch", time: "13:30", icon: Coffee },
    { label: "Before dinner", time: "19:30", icon: Moon },
    { label: "After dinner", time: "20:30", icon: Moon },
    { label: "Bedtime", time: "22:00", icon: Moon },
];

const TYPES = ["tablet", "capsule", "syrup", "injection", "inhaler", "drops", "ointment"];
const FOOD_OPTS = [
    { id: "before_food", label: "Before food" },
    { id: "after_food", label: "After food" },
    { id: "with_food", label: "With food" },
    { id: "empty_stomach", label: "Empty stomach" },
    { id: "n/a", label: "Doesn't matter" },
];

const Stepper = ({ step, total = 6 }) => (
    <ol className="mb-8 flex items-center gap-1" data-testid="add-wizard-stepper">
        {[...Array(total)].map((_, i) => {
            const idx = i + 1;
            const active = step === idx;
            const done = step > idx;
            return (
                <li key={idx} className="flex flex-1 items-center">
                    <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold ${done ? "bg-emerald-500 text-white" : active ? "bg-brand-700 text-white" : "bg-zinc-200 text-zinc-500"}`}>
                        {done ? <CheckCircle2 className="h-4 w-4" /> : idx}
                    </span>
                    {idx < total && <span className={`mx-1 h-px flex-1 ${done ? "bg-emerald-300" : "bg-zinc-200"}`} />}
                </li>
            );
        })}
    </ol>
);

const AddMedicineWizardContent = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);

    // Step 1
    const [sourceMode, setSourceMode] = useState(null);
    // Step 1 — catalog search
    const [catalogQuery, setCatalogQuery] = useState("");
    const [catalogResults, setCatalogResults] = useState([]);
    const [searching, setSearching] = useState(false);
    // Step 1 — barcode
    const [barcode, setBarcode] = useState("");
    // Step 1 — existing Rx
    const [rxList, setRxList] = useState([]);
    const [selectedRx, setSelectedRx] = useState(null);

    // Step 2 — medicine details
    const [medName, setMedName] = useState("");
    const [strength, setStrength] = useState("");
    const [type, setType] = useState("tablet");
    const [dosage, setDosage] = useState("1 tablet");
    const [productId, setProductId] = useState(null);

    // Step 3 — Rx details
    const [rxDoctor, setRxDoctor] = useState("");
    const [rxHospital, setRxHospital] = useState("");
    const [rxStartDate, setRxStartDate] = useState(new Date().toISOString().slice(0, 10));
    const [rxEndDate, setRxEndDate] = useState("");
    const [linkedRxId, setLinkedRxId] = useState(null);

    // Step 4 — schedule
    const [scheduleType, setScheduleType] = useState("daily");
    const [daysOfWeek, setDaysOfWeek] = useState([0, 1, 2, 3, 4, 5, 6]);
    const [slots, setSlots] = useState([{ label: "After breakfast", time: "08:30", enabled: true }]);
    const [foodRel, setFoodRel] = useState("after_food");
    const [criticalOverride, setCriticalOverride] = useState(null);

    // Step 5 — refill
    const [refillEnabled, setRefillEnabled] = useState(true);
    const [refillQty, setRefillQty] = useState(30);
    const [refillDaily, setRefillDaily] = useState(1);

    const [submitting, setSubmitting] = useState(false);

    // --- Step 1: Catalog search
    useEffect(() => {
        if (sourceMode !== "catalog") return;
        if (catalogQuery.trim().length < 2) { setCatalogResults([]); return; }
        const t = setTimeout(async () => {
            setSearching(true);
            try {
                const { data } = await api.get("/products", { params: { q: catalogQuery.trim(), page_size: 8 } });
                setCatalogResults(data?.items || []);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [catalogQuery, sourceMode]);

    // --- Step 1: Prescription list
    useEffect(() => {
        if (sourceMode !== "prescription_existing") return;
        (async () => {
            try {
                const { data } = await api.get("/prescriptions");
                setRxList((data?.items || data || []).filter((r) => r.ocr_status === "completed" && (r.medicines_count || 0) > 0));
            } catch { /* ignore */ }
        })();
    }, [sourceMode]);

    const pickCatalogProduct = (p) => {
        setMedName(p.name);
        setStrength(p.pack_size || "");
        setProductId(p.id);
        setStep(2);
    };

    const tryBarcode = async () => {
        if (barcode.trim().length < 4) return;
        try {
            const { data } = await api.post("/reminder/medications/barcode-scan", { barcode_value: barcode.trim() });
            if (data.matched && data.prefill) {
                setMedName(data.prefill.medicine_name || "");
                setStrength(data.prefill.strength || "");
                setProductId(data.prefill.product_id || null);
                toast.success("Matched: " + data.prefill.medicine_name);
                setStep(2);
            } else {
                toast.info(data.message || "No match — please enter manually");
                setSourceMode("manual");
                setStep(2);
            }
        } catch {
            toast.error("Lookup failed");
        }
    };

    const createFromRx = async () => {
        if (!selectedRx) return;
        try {
            setSubmitting(true);
            const { data } = await api.post(`/reminder/medications/from-prescription/${selectedRx}`);
            toast.success(`Created ${data.created} medications from prescription`);
            navigate("/reminders");
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Failed to import");
        } finally {
            setSubmitting(false);
        }
    };

    const toggleDay = (d) => setDaysOfWeek((arr) => arr.includes(d) ? arr.filter((x) => x !== d) : [...arr, d].sort());

    const addSlot = (preset) => {
        setSlots((arr) => [...arr, { ...preset, enabled: true }]);
    };
    const removeSlot = (idx) => setSlots((arr) => arr.filter((_, i) => i !== idx));
    const setSlotTime = (idx, t) => setSlots((arr) => arr.map((s, i) => i === idx ? { ...s, time: t } : s));

    const submitAll = async () => {
        if (!medName.trim()) {
            toast.error("Medicine name is required");
            setStep(2);
            return;
        }
        try {
            setSubmitting(true);
            // 1. Create medication
            const { data: med } = await api.post("/reminder/medications", {
                medicine_name: medName.trim(),
                strength: strength || null,
                medicine_type: type,
                dosage_quantity: dosage || null,
                source: sourceMode === "catalog" ? "catalog" : (sourceMode === "barcode" ? "barcode" : "manual"),
                product_id: productId,
                prescription_id: linkedRxId,
                prescription_details: {
                    doctor_name: rxDoctor || null,
                    hospital_name: rxHospital || null,
                    start_date: rxStartDate || null,
                    end_date: rxEndDate || null,
                    prescription_upload_id: linkedRxId,
                },
            });
            // 2. Create schedule
            await api.post(`/reminder/medications/${med.id}/schedules`, {
                schedule_type: scheduleType,
                days_of_week: scheduleType === "weekly" ? daysOfWeek : null,
                time_slots: slots.filter((s) => s.time),
                relation_to_food: foodRel,
                critical: criticalOverride,
                start_date: rxStartDate || null,
                end_date: rxEndDate || null,
            });
            // 3. Refill tracking (optional)
            if (refillEnabled && refillQty > 0 && refillDaily > 0) {
                await api.post(`/reminder/medications/${med.id}/refill-tracking`, {
                    quantity_purchased: Number(refillQty),
                    daily_consumption: Number(refillDaily),
                });
            }
            toast.success("Medication added — we'll remind you on time!");
            navigate(`/reminders/${med.id}`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not save");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="add-medicine-wizard">
            <Stepper step={step} />

            {/* Step 1 — Source */}
            {step === 1 && (
                <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft sm:p-8" data-testid="wizard-step-source">
                    <div className="mb-5">
                        <h2 className="text-lg font-semibold text-zinc-900">How do you want to add this medicine?</h2>
                        <p className="text-sm text-zinc-500">Pick the fastest path for you.</p>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {SOURCES.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => setSourceMode(s.id)}
                                className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition ${sourceMode === s.id ? "border-brand-600 bg-brand-50" : "border-zinc-200 hover:border-brand-300"}`}
                                data-testid={`source-${s.id}`}
                            >
                                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-brand-700 ring-1 ring-zinc-100">
                                    <s.icon className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-semibold text-zinc-900">{s.label}</p>
                                    <p className="mt-0.5 text-xs text-zinc-500">{s.blurb}</p>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Inline interaction per source */}
                    {sourceMode === "catalog" && (
                        <div className="mt-5">
                            <input
                                value={catalogQuery}
                                onChange={(e) => setCatalogQuery(e.target.value)}
                                placeholder="Search by name… (e.g. Paracetamol, Telmisartan)"
                                className="w-full rounded-2xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-brand-500"
                                data-testid="wizard-catalog-search"
                            />
                            <ul className="mt-2 space-y-1">
                                {catalogResults.map((p) => (
                                    <li key={p.id}>
                                        <button
                                            onClick={() => pickCatalogProduct(p)}
                                            className="flex w-full items-center justify-between rounded-xl border border-zinc-100 px-3 py-2 text-left text-sm hover:border-brand-300"
                                            data-testid={`wizard-catalog-result-${p.id}`}
                                        >
                                            <span className="font-medium text-zinc-900">{p.name}</span>
                                            <span className="text-xs text-zinc-500">{p.pack_size || ""}</span>
                                        </button>
                                    </li>
                                ))}
                                {searching && <li className="text-xs text-zinc-500">Searching…</li>}
                                {!searching && catalogQuery.length >= 2 && catalogResults.length === 0 && (
                                    <li className="text-xs text-zinc-500">No results — try a different name.</li>
                                )}
                            </ul>
                        </div>
                    )}
                    {sourceMode === "barcode" && (
                        <div className="mt-5">
                            <input
                                value={barcode}
                                onChange={(e) => setBarcode(e.target.value)}
                                placeholder="Type or paste barcode value"
                                className="w-full rounded-2xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-brand-500"
                                data-testid="wizard-barcode-input"
                            />
                            <div className="mt-2 flex justify-end">
                                <button onClick={tryBarcode} className="btn-brand !py-2 !px-4 !text-sm" data-testid="wizard-barcode-lookup">Look up</button>
                            </div>
                            <p className="mt-2 text-xs text-zinc-500">Tip: Barcode scanning via camera is coming in the mobile app. For now, type the code from the box.</p>
                        </div>
                    )}
                    {sourceMode === "prescription_existing" && (
                        <div className="mt-5">
                            {rxList.length === 0 ? (
                                <p className="text-sm text-zinc-500">No verified prescriptions found. <Link to="/prescriptions" className="text-brand-700 underline">Upload one</Link>.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {rxList.map((r) => (
                                        <li key={r.id}>
                                            <button
                                                onClick={() => setSelectedRx(r.id)}
                                                className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${selectedRx === r.id ? "border-brand-600 bg-brand-50" : "border-zinc-100 hover:border-brand-300"}`}
                                                data-testid={`wizard-rx-${r.id}`}
                                            >
                                                <span>
                                                    <span className="font-medium text-zinc-900">{r.doctor_name || "Prescription"}</span>
                                                    <span className="ml-2 text-xs text-zinc-500">{r.prescription_date} · {r.medicines_count} medicine(s)</span>
                                                </span>
                                                <span className="text-[10px] text-emerald-700">verified</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="mt-3 flex justify-end">
                                <button
                                    onClick={createFromRx}
                                    disabled={!selectedRx || submitting}
                                    className="btn-brand"
                                    data-testid="wizard-rx-import"
                                >
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                                    Import all medicines
                                </button>
                            </div>
                        </div>
                    )}

                    {sourceMode === "manual" && (
                        <div className="mt-5 rounded-2xl border border-dashed border-zinc-200 p-4 text-center text-sm text-zinc-500">
                            We&apos;ll ask for the details on the next step.
                        </div>
                    )}

                    <div className="mt-6 flex justify-between">
                        <Link to="/reminders" className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-5 py-2.5 text-sm font-medium text-zinc-700 hover:border-zinc-300" data-testid="wizard-cancel">
                            <ArrowLeft className="h-4 w-4" /> Cancel
                        </Link>
                        <button
                            onClick={() => setStep(2)}
                            disabled={!sourceMode || sourceMode === "prescription_existing"}
                            className="btn-brand"
                            data-testid="wizard-source-next"
                        >
                            Continue <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Step 2 — Medicine details */}
            {step === 2 && (
                <WizardCard step={step} setStep={setStep} title="Medicine details" subtitle="What are you taking?" testId="wizard-step-details" onNext={() => setStep(3)}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="Medicine name *">
                            <input
                                value={medName}
                                onChange={(e) => setMedName(e.target.value)}
                                placeholder="e.g. Telmisartan"
                                className="input-base"
                                data-testid="wizard-med-name"
                            />
                        </Field>
                        <Field label="Strength">
                            <input
                                value={strength}
                                onChange={(e) => setStrength(e.target.value)}
                                placeholder="e.g. 40mg"
                                className="input-base"
                                data-testid="wizard-med-strength"
                            />
                        </Field>
                        <Field label="Type">
                            <select value={type} onChange={(e) => setType(e.target.value)} className="input-base" data-testid="wizard-med-type">
                                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Dose per intake">
                            <input
                                value={dosage}
                                onChange={(e) => setDosage(e.target.value)}
                                placeholder="e.g. 1 tablet · 5ml"
                                className="input-base"
                                data-testid="wizard-med-dosage"
                            />
                        </Field>
                    </div>
                </WizardCard>
            )}

            {/* Step 3 — Prescription details */}
            {step === 3 && (
                <WizardCard step={step} setStep={setStep} title="Prescription details (optional)" subtitle="Help us track doctor, hospital, and duration." testId="wizard-step-rx" onNext={() => setStep(4)}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="Doctor"><input value={rxDoctor} onChange={(e) => setRxDoctor(e.target.value)} placeholder="Dr. Sharma" className="input-base" data-testid="wizard-rx-doctor" /></Field>
                        <Field label="Hospital / clinic"><input value={rxHospital} onChange={(e) => setRxHospital(e.target.value)} placeholder="Apollo Clinic" className="input-base" data-testid="wizard-rx-hospital" /></Field>
                        <Field label="Start date"><input type="date" value={rxStartDate} onChange={(e) => setRxStartDate(e.target.value)} className="input-base" data-testid="wizard-rx-start" /></Field>
                        <Field label="End date (course duration)"><input type="date" value={rxEndDate} onChange={(e) => setRxEndDate(e.target.value)} className="input-base" data-testid="wizard-rx-end" /></Field>
                    </div>
                    <p className="mt-4 text-xs text-zinc-500">Leave end date blank for indefinite / lifelong medications.</p>
                </WizardCard>
            )}

            {/* Step 4 — Schedule */}
            {step === 4 && (
                <WizardCard step={step} setStep={setStep} title="Schedule" subtitle="When do you take this medicine?" testId="wizard-step-schedule" onNext={() => setStep(5)} disableNext={slots.filter((s) => s.time).length === 0}>
                    {/* Frequency */}
                    <Field label="Frequency">
                        <div className="flex flex-wrap gap-1.5" data-testid="wizard-schedule-type">
                            {[
                                { id: "daily", label: "Daily" },
                                { id: "alternate_days", label: "Alternate days" },
                                { id: "weekly", label: "Weekly" },
                            ].map((opt) => (
                                <button
                                    key={opt.id}
                                    onClick={() => setScheduleType(opt.id)}
                                    className={`rounded-full border px-3 py-1 text-xs font-medium ${scheduleType === opt.id ? "border-brand-600 bg-brand-600 text-white" : "border-zinc-200 hover:border-brand-300"}`}
                                    data-testid={`wizard-schedule-${opt.id}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </Field>
                    {scheduleType === "weekly" && (
                        <Field label="Days of week">
                            <div className="flex flex-wrap gap-1.5">
                                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
                                    <button
                                        key={d}
                                        onClick={() => toggleDay(i)}
                                        className={`rounded-full border px-3 py-1 text-xs ${daysOfWeek.includes(i) ? "border-brand-600 bg-brand-600 text-white" : "border-zinc-200"}`}
                                        data-testid={`wizard-dow-${i}`}
                                    >
                                        {d}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    )}

                    {/* Time slots */}
                    <Field label="Time slots">
                        <div className="space-y-2" data-testid="wizard-slots">
                            {slots.map((s, idx) => (
                                <div key={idx} className="flex items-center gap-2 rounded-xl bg-zinc-50/40 px-3 py-2">
                                    <Clock className="h-4 w-4 shrink-0 text-zinc-400" />
                                    <input
                                        value={s.label}
                                        onChange={(e) => setSlots((arr) => arr.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                                        className="flex-1 bg-transparent text-sm outline-none"
                                        data-testid={`wizard-slot-label-${idx}`}
                                    />
                                    <input
                                        type="time"
                                        value={s.time}
                                        onChange={(e) => setSlotTime(idx, e.target.value)}
                                        className="rounded-md border border-zinc-200 px-2 py-1 text-xs"
                                        data-testid={`wizard-slot-time-${idx}`}
                                    />
                                    <button onClick={() => removeSlot(idx)} className="text-zinc-400 hover:text-rose-600" aria-label="Remove slot" data-testid={`wizard-slot-remove-${idx}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                            <div className="flex flex-wrap gap-1.5">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Quick add:</span>
                                {SLOT_PRESETS.map((p) => (
                                    <button
                                        key={p.label}
                                        onClick={() => addSlot(p)}
                                        className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11px] hover:border-brand-300"
                                        data-testid={`wizard-slot-add-${p.label.replace(/\s+/g, '-').toLowerCase()}`}
                                    >
                                        <Plus className="h-3 w-3" /> {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Field>

                    <Field label="Food relation">
                        <div className="flex flex-wrap gap-1.5">
                            {FOOD_OPTS.map((f) => (
                                <button
                                    key={f.id}
                                    onClick={() => setFoodRel(f.id)}
                                    className={`rounded-full border px-3 py-1 text-xs ${foodRel === f.id ? "border-brand-600 bg-brand-600 text-white" : "border-zinc-200"}`}
                                    data-testid={`wizard-food-${f.id}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <Field label="Critical (escalates if missed)">
                        <div className="flex items-center gap-3">
                            <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
                                <input
                                    type="checkbox"
                                    checked={criticalOverride ?? false}
                                    onChange={(e) => setCriticalOverride(e.target.checked)}
                                    className="h-4 w-4 rounded border-zinc-300"
                                    data-testid="wizard-critical-toggle"
                                />
                                Force critical
                            </label>
                            <span className="text-xs text-zinc-500">Auto-detected from medicine name (e.g. insulin, BP meds).</span>
                        </div>
                    </Field>
                </WizardCard>
            )}

            {/* Step 5 — Refill */}
            {step === 5 && (
                <WizardCard step={step} setStep={setStep} title="Refill tracking" subtitle="We'll remind you to reorder before you run out." testId="wizard-step-refill" onNext={() => setStep(6)}>
                    <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={refillEnabled}
                            onChange={(e) => setRefillEnabled(e.target.checked)}
                            className="h-4 w-4 rounded border-zinc-300"
                            data-testid="wizard-refill-toggle"
                        />
                        Enable refill tracking
                    </label>
                    {refillEnabled && (
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <Field label="Pills purchased">
                                <input type="number" min="1" value={refillQty} onChange={(e) => setRefillQty(e.target.value)} className="input-base" data-testid="wizard-refill-qty" />
                            </Field>
                            <Field label="Daily consumption">
                                <input type="number" min="0.5" step="0.5" value={refillDaily} onChange={(e) => setRefillDaily(e.target.value)} className="input-base" data-testid="wizard-refill-daily" />
                            </Field>
                        </div>
                    )}
                    {refillEnabled && refillQty > 0 && refillDaily > 0 && (
                        <p className="mt-3 text-xs text-zinc-500">≈ {Math.floor(refillQty / refillDaily)} days of supply. We&apos;ll alert at 10 / 7 / 3 / 1 day(s) left.</p>
                    )}
                </WizardCard>
            )}

            {/* Step 6 — Confirm */}
            {step === 6 && (
                <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white p-6 shadow-soft sm:p-8" data-testid="wizard-step-confirm">
                    <h2 className="text-lg font-semibold text-zinc-900">Review &amp; save</h2>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Summary label="Medicine" value={`${medName} ${strength}`} />
                        <Summary label="Form" value={`${type} · ${dosage}`} />
                        <Summary label="Source" value={sourceMode || "manual"} />
                        <Summary label="Schedule" value={`${scheduleType} · ${slots.length} time slot(s)`} />
                        <Summary label="Food" value={foodRel} />
                        <Summary label="Refill" value={refillEnabled ? `${refillQty} pills, ${refillDaily}/day` : "tracking off"} />
                        <Summary label="Doctor" value={rxDoctor || "—"} />
                        <Summary label="Course" value={[rxStartDate, rxEndDate].filter(Boolean).join(" → ") || "ongoing"} />
                    </div>
                    <div className="mt-6 flex justify-between">
                        <button
                            onClick={() => setStep(5)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-5 py-2.5 text-sm font-medium text-zinc-700 hover:border-zinc-300"
                            data-testid="wizard-confirm-back"
                        >
                            <ArrowLeft className="h-4 w-4" /> Back
                        </button>
                        <button
                            onClick={submitAll}
                            disabled={submitting || !medName.trim()}
                            className="btn-brand"
                            data-testid="wizard-submit"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            {submitting ? "Saving…" : "Save medication"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const WizardCard = ({ step, setStep, title, subtitle, testId, onNext, children, disableNext }) => (
    <div className="rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft sm:p-8" data-testid={testId}>
        <div className="mb-4">
            <h2 className="text-lg font-semibold text-zinc-900">{title}</h2>
            {subtitle && <p className="text-sm text-zinc-500">{subtitle}</p>}
        </div>
        <div className="space-y-4">{children}</div>
        <div className="mt-6 flex justify-between">
            <button
                onClick={() => setStep(step - 1)}
                className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-5 py-2.5 text-sm font-medium text-zinc-700 hover:border-zinc-300"
                data-testid={`wizard-back-${step}`}
            >
                <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
                onClick={onNext}
                disabled={!!disableNext}
                className="btn-brand"
                data-testid={`wizard-next-${step}`}
            >
                Continue <ArrowRight className="h-4 w-4" />
            </button>
        </div>
    </div>
);

const Field = ({ label, children }) => (
    <label className="block">
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);

const Summary = ({ label, value }) => (
    <div className="rounded-xl bg-white p-3 ring-1 ring-zinc-100">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="mt-0.5 text-sm font-medium text-zinc-900">{value || "—"}</p>
    </div>
);

const AddMedicineWizard = () => (
    <Layout>
        <section className="mx-auto max-w-3xl px-6 py-10 lg:px-12">
            <Link to="/reminders" className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700" data-testid="wizard-back-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to reminders
            </Link>
            <header className="mb-8">
                <p className="label-uppercase">PILLS Reminder</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Add a medication</h1>
            </header>
            <AuthGuard>
                <AddMedicineWizardContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default AddMedicineWizard;
